import {
  RiskModelSchema,
  type CapabilityModel,
  type Operation,
  type OperationRisk,
  type RiskLevel,
  type RiskModel,
  type DataSensitivity
} from "@keystone/core";

/**
 * Rule-based RiskModel deriver. Heuristics anchored to OpenAPI semantics +
 * operationId verb hints + OWASP MCP / LLM categories.
 *
 * Level taxonomy:
 *  - "read"        : safe; GET/HEAD/OPTIONS or explicit get/list/search verbs.
 *  - "mutate"      : reversible state change; POST/PUT/PATCH without irreversible verbs.
 *  - "destructive" : irreversible / high-blast-radius (DELETE or charge/transfer/refund/send/...).
 *
 * Irreversibility is orthogonal to level: a "mutate" can be effectively irreversible
 * if no rollback path exists. We bias toward marking irreversibility broadly because
 * the cost of false-positive is small (extra scope check); the cost of false-negative
 * is an agent that wipes prod.
 */

const READ_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

const IRREVERSIBLE_VERB_PATTERNS = [
  /\b(delete|destroy|purge|drop|truncate)\b/i,
  /\b(charge|capture|refund|transfer|payout|wire)\b/i,
  /\b(send|publish|broadcast|deploy|release)\b/i,
  /\b(cancel|terminate|revoke|expire)\b/i,
  /\b(execute|run|trigger|fire)\b/i
];

/**
 * POST verbs / path hints that are *not* state-changing. Many APIs expose search /
 * validate / preview as POST because the input payload is too big for a GET querystring,
 * but the operation is read-only. Classifying these as `mutate` would over-scope the
 * generated MCP tool — the F7 bug from the Phase-1 review.
 */
const NON_MUTATING_POST_VERBS = new Set([
  "search",
  "query",
  "find",
  "lookup",
  "resolve",
  "validate",
  "verify",
  "check",
  "exists",
  "preview",
  "dryrun",
  "simulate",
  "evaluate",
  "match",
  "score",
  "calculate",
  "compute"
]);

const PII_KEY_PATTERN = /\b(email|phone|address|ssn|dob|birth(date)?|firstName|lastName|fullName|userId)\b/i;
const PAYMENT_KEY_PATTERN =
  /\b(amount(Cents)?|card(Number)?|cvv|iban|paymentMethod|stripeToken|paypal|amount)\b/i;
const CREDENTIAL_KEY_PATTERN = /\b(password|secret|token|apiKey|api_key|privateKey|accessToken)\b/i;
const PHI_KEY_PATTERN = /\b(diagnosis|patient|prescription|medical|health|record)\b/i;
const CONFIG_KEY_PATTERN = /\b(config|setting|policy|webhook|environment)\b/i;

export function deriveRiskModel(capability: CapabilityModel): RiskModel {
  const operations = capability.operations.map((op) => classify(op));
  const summary = {
    read: operations.filter((o) => o.level === "read").length,
    mutate: operations.filter((o) => o.level === "mutate").length,
    destructive: operations.filter((o) => o.level === "destructive").length,
    irreversibleCount: operations.filter((o) => o.irreversible).length
  };
  return RiskModelSchema.parse({ operations, summary });
}

function classify(op: Operation): OperationRisk {
  const verb = pickVerb(op);
  const level = classifyLevel(op, verb);
  const irreversible = level === "destructive" || hasIrreversibleVerb(op);
  const dataSensitivity = inferSensitivity(op);
  const crossTenant = mentionsCrossTenant(op);
  const tag = op.tags[0] ?? "api";
  const scopeRoot = tag.toLowerCase().replace(/[^a-z0-9]+/g, "_");
  const suggestedScopes = suggestScopes(scopeRoot, level, verb, irreversible);

  return {
    operationId: op.id,
    level,
    irreversible,
    dataSensitivity,
    crossTenant,
    rationale: buildRationale(op, level, verb, irreversible, dataSensitivity),
    suggestedScopes,
    owaspMcp: owaspMcpTags(level, dataSensitivity, irreversible),
    mitreAtlas: mitreAtlasTags(level, dataSensitivity)
  };
}

function classifyLevel(op: Operation, verb: string): RiskLevel {
  if (READ_METHODS.has(op.method)) return "read";
  if (op.method === "DELETE") return "destructive";
  if (hasIrreversibleVerb(op)) return "destructive";
  if (/^(get|list|fetch|read|count)$/i.test(verb)) return "read";
  // POST /search, POST /validate etc. are read-only despite the HTTP method.
  if (op.method === "POST" && NON_MUTATING_POST_VERBS.has(verb.toLowerCase())) return "read";
  if (op.method === "POST" && /\/(search|query|find|lookup|validate|preview|dryRun|dry_run|exists)\b/i.test(op.path))
    return "read";
  return "mutate";
}

function hasIrreversibleVerb(op: Operation): boolean {
  const haystack = `${op.id} ${op.path} ${op.summary ?? ""} ${op.description ?? ""}`;
  return IRREVERSIBLE_VERB_PATTERNS.some((re) => re.test(haystack));
}

function pickVerb(op: Operation): string {
  // operationId is typically camelCase: "createPet" → verb "create".
  const match = op.id.match(/^[a-z]+/);
  return match ? match[0] : "";
}

function inferSensitivity(op: Operation): DataSensitivity[] {
  const tokens = collectSchemaKeys(op);
  const out = new Set<DataSensitivity>();
  for (const tok of tokens) {
    if (PII_KEY_PATTERN.test(tok)) out.add("pii");
    if (PAYMENT_KEY_PATTERN.test(tok)) out.add("payment");
    if (CREDENTIAL_KEY_PATTERN.test(tok)) out.add("credentials");
    if (PHI_KEY_PATTERN.test(tok)) out.add("phi");
    if (CONFIG_KEY_PATTERN.test(tok)) out.add("config");
  }
  return Array.from(out);
}

function collectSchemaKeys(op: Operation): string[] {
  const out: string[] = [];
  const push = (k: string): void => {
    out.push(k);
    // Split camelCase / snake_case / kebab-case into atom tokens too so the
    // regex patterns (which use \b word boundaries) match property names like
    // `ownerEmail`, `payment_method_id`, or `stripe-token`.
    const atoms = k
      .replace(/[_-]+/g, " ")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .split(/\s+/)
      .filter(Boolean);
    if (atoms.length > 1) out.push(...atoms);
  };
  const walk = (node: unknown): void => {
    if (!node || typeof node !== "object") return;
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    for (const [k, v] of Object.entries(node)) {
      push(k);
      walk(v);
    }
  };
  walk(op.requestBody?.schema);
  walk(op.parameters.map((p) => ({ [p.name]: p.schema })));
  walk(op.responses.map((r) => r.schema));
  return out;
}

function mentionsCrossTenant(op: Operation): boolean {
  const hay = `${op.path} ${op.summary ?? ""} ${op.description ?? ""}`;
  return /\b(tenant|org(anization)?|account|workspace|team)Id\b/i.test(hay);
}

function suggestScopes(root: string, level: RiskLevel, verb: string, irreversible: boolean): string[] {
  const scopes: string[] = [];
  if (level === "read") scopes.push(`${root}.read`);
  if (level === "mutate") scopes.push(`${root}.write`);
  if (level === "destructive") {
    if (/^delete|destroy|purge|drop|truncate/i.test(verb)) scopes.push(`${root}.delete`);
    else scopes.push(`${root}.write`, `${root}:${verb || "execute"}`);
  }
  if (irreversible && level !== "destructive") scopes.push(`${root}.write`);
  return Array.from(new Set(scopes));
}

function buildRationale(
  op: Operation,
  level: RiskLevel,
  verb: string,
  irreversible: boolean,
  ds: DataSensitivity[]
): string {
  const parts: string[] = [
    `HTTP ${op.method} ${op.path}`,
    `operationId verb "${verb}"`,
    `classified as ${level}`
  ];
  if (irreversible) parts.push("flagged irreversible");
  if (ds.length > 0) parts.push(`data sensitivity: ${ds.join(", ")}`);
  return parts.join("; ");
}

/**
 * OWASP MCP Top 10 (2025) — official mapping.
 *  MCP01 Token Mismanagement                — every authed tool
 *  MCP02 Privilege Escalation / Scope Creep — non-read tools with mutating scope
 *  MCP03 Tool Poisoning                     — tools with model-facing descriptions (all of them)
 *  MCP04 Supply Chain                       — server-level (not per-op)
 *  MCP05 Command Injection                  — tools that pass strings into shell/SQL/template
 *  MCP06 Intent Flow Subversion             — destructive / irreversible / multi-step tools
 *  MCP07 Insufficient AuthN/Z               — every tool
 *  MCP08 Lack of Audit                      — every tool (we log; mapping signals "must be logged")
 *  MCP09 Shadow Servers                     — server-level (not per-op)
 *  MCP10 Context Over-Sharing               — tools returning PII / payment / credentials / PHI
 */
function owaspMcpTags(level: RiskLevel, ds: DataSensitivity[], irreversible: boolean): string[] {
  const tags = new Set<string>(["MCP01", "MCP03", "MCP07", "MCP08"]);
  if (level !== "read") tags.add("MCP02");
  if (level === "destructive" || irreversible) tags.add("MCP06");
  if (ds.some((d) => d === "pii" || d === "payment" || d === "credentials" || d === "phi"))
    tags.add("MCP10");
  return Array.from(tags).sort();
}

/** MITRE ATLAS mapping (partial). AML.T0051 = LLM Prompt Injection; AML.T0048 = External harms. */
function mitreAtlasTags(level: RiskLevel, ds: DataSensitivity[]): string[] {
  const tags = new Set<string>(["AML.T0051"]);
  if (level === "destructive") tags.add("AML.T0048");
  if (ds.includes("credentials")) tags.add("AML.T0024.000");
  return Array.from(tags);
}
